import React, { useState, useEffect } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useSpring,
  MotionValue,
} from "framer-motion";

declare global {
  interface Window {
    supabase: any;
  }
}

const createClient = window.supabase?.createClient;
const supabaseUrl = "https://xsxnltxafwpyarasrlgq.supabase.co";
const supabaseAnonKey =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhzeG5sdHhhZndweWFyYXNybGdxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzg4OTAzMDYsImV4cCI6MjA5NDQ2NjMwNn0.53Vk9xpxk47mbeJkW1eagGyleppCxdpDSG5u5_P0s2c";

export const supabase =
  createClient && supabaseUrl && supabaseAnonKey
    ? createClient(supabaseUrl, supabaseAnonKey)
    : null;

const TARGET_DATE = new Date("March 21, 2027 00:00:00").getTime();

interface Star {
  id: string | number;
  message: string;
  x: number;
  y: number;
  drift: number;
  size: number;
}

interface Constellation {
  id: string;
  name: string;
  messages: Star[];
  x: number;
  y: number;
  drift: number;
}

interface Galaxy {
  id: string;
  name: string;
  data: Constellation[];
  x: number;
  y: number;
  rotationSpeed: number;
}

interface StarNoteProps {
  n: Star;
  highlightedId: string | number | null;
  starPointOpacity: MotionValue<number>;
  cinematicSpring: { stiffness: number; damping: number; restDelta: number };
  starsClickable: MotionValue<string>;
}

const truncateText = (text: string, maxLength: number = 25) => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + "...";
};

// ========================================================
// CONSTELLATION POOLS (Locked)
// ========================================================
const GREEK_PREFIXES = [
  "Alpha",
  "Beta",
  "Gamma",
  "Delta",
  "Epsilon",
  "Zeta",
  "Eta",
  "Theta",
  "Iota",
  "Kappa",
  "Lambda",
  "Mu",
  "Nu",
  "Xi",
  "Omicron",
  "Pi",
  "Rho",
  "Sigma",
  "Tau",
  "Upsilon",
  "Phi",
  "Chi",
  "Psi",
  "Omega",
];

const COSMIC_NAMES = [
  "Andromeda",
  "Orion",
  "Lyra",
  "Cygnus",
  "Cassiopeia",
  "Perseus",
  "Pegasus",
  "Draco",
  "Aquila",
  "Phoenix",
  "Centaurus",
  "Carina",
  "Crux",
  "Hydra",
  "Monoceros",
  "Octans",
  "Pavonis",
  "Serpens",
  "Ursa",
  "Vela",
  "Aether",
  "Helios",
  "Selene",
  "Nyx",
  "Chronos",
  "Gaia",
  "Erebus",
  "Hyperion",
  "Nebula",
  "Quasar",
  "Pulsar",
  "Zenith",
  "Nadir",
  "Eclipse",
  "Solstice",
  "Equinox",
  "Horizon",
  "Infinity",
  "Obsidian",
  "Elysium",
];

// ========================================================
// REAL-BASED INDEPENDENT GALAXY POOLS (Strict 2-Word Pairs)
// ========================================================
const GALAXY_PREFIXES = [
  "Messier",
  "Kepler",
  "Calvera",
  "Cosmos",
  "Quasar",
  "Supernova",
  "Nebula",
  "Pulsar",
  "Exoplanet",
  "Hubble",
  "Webb",
  "Milky",
  "Giga",
];

const GALAXY_NAMES = [
  "Centauri",
  "Sagittarius",
  "Persei",
  "Cygnus",
  "Borealis",
  "Australis",
  "Majoris",
  "Minoris",
  "Eridani",
  "Pegasi",
  "Orionis",
  "Draconis",
  "Ceti",
];

// Generates a unique, seed-stable constellation name based on item database IDs
const getDeterministicConstellationName = (seedId: string | number): string => {
  const seedStr = String(seedId);
  let hash = 0;
  for (let i = 0; i < seedStr.length; i++) {
    hash = seedStr.charCodeAt(i) + ((hash << 5) - hash);
  }
  const prefixIndex = Math.abs(hash) % GREEK_PREFIXES.length;
  const nameIndex = Math.abs(hash >> 3) % COSMIC_NAMES.length;
  return `${GREEK_PREFIXES[prefixIndex]} ${COSMIC_NAMES[nameIndex]}`;
};

// Generates a unique, seed-stable macrocosm galaxy name using real astronomical catalog pools
const getDeterministicGalaxyName = (seedId: string | number): string => {
  const seedStr = String(seedId);
  let hash = 0;
  for (let i = 0; i < seedStr.length; i++) {
    hash = seedStr.charCodeAt(i) + ((hash << 5) - hash);
  }
  const galaxyPrefixIndex = Math.abs(hash >> 5) % GALAXY_PREFIXES.length;
  const galaxyNameIndex = Math.abs(hash >> 2) % GALAXY_NAMES.length;

  return `${GALAXY_PREFIXES[galaxyPrefixIndex]} ${GALAXY_NAMES[galaxyNameIndex]}`;
};

const StarNote = ({
  n,
  highlightedId,
  starPointOpacity,
  cinematicSpring,
  starsClickable,
}: StarNoteProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const isHighlighted = n.id === highlightedId;
  const xTranslation = n.x > 80 ? "-85%" : n.x < 20 ? "-15%" : "-50%";
  const isTopHalf = n.y < 50;
  const verticalClass = isTopHalf ? "top-12" : "bottom-12";
  const isVisible = isHighlighted || isHovered;

  // DETERMINISTIC SEED FOR GENTLE, SLOW SHIMMERS
  // Increased the duration variables to make the cycles much longer and peaceful
  const uniqueSeed = typeof n.id === "number" ? n.id : n.x + n.y;
  const twinkleDuration = 3.5 + (uniqueSeed % 3); // Now ranges safely between 3.5s and 6.5s
  const twinkleDelay = (uniqueSeed % 2) * 1.2;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      className="absolute"
      style={{ left: `${n.x}%`, top: `${n.y}%` }}
    >
      <div
        style={{
          pointerEvents: starsClickable.get() === "none" ? "none" : "auto",
        }}
        className="relative flex items-center justify-center w-12 h-12 -m-6 cursor-pointer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* THE GENTLE AMBIENT COSMIC SHIMMER */}
        <motion.div
          className="bg-white rounded-full shadow-[0_0_8px_rgba(255,255,255,0.6)]"
          style={{
            width: `${(n.size || 2) * 1.0}px`,
            height: `${(n.size || 2) * 1.0}px`,
          }}
          // Scale is now perfectly flat unless hovered, and opacity has a narrow, soft curve
          animate={{
            scale: isVisible ? 2.5 : 1,
            opacity: isVisible ? 1 : [0.4, 0.7, 0.4],
          }}
          transition={{
            scale: isVisible
              ? { type: "spring", stiffness: 300, damping: 20 }
              : { duration: 0.3 },
            opacity: isVisible
              ? { duration: 0.2 }
              : {
                  duration: twinkleDuration,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: twinkleDelay,
                },
          }}
        />

        {/* HOVER / HIGHLIGHT MESSAGE ELEMENT */}
        <motion.div
          initial={false}
          animate={{
            opacity: isVisible ? 1 : 0,
            scale: isVisible ? 1 : 0.9,
            y: isVisible ? 0 : isTopHalf ? 10 : -10,
          }}
          transition={cinematicSpring}
          style={{ translateX: xTranslation }}
          className={`absolute ${verticalClass} w-[260px] max-w-[85vw] left-1/2 z-[100] pointer-events-none`}
        >
          <div className="backdrop-blur-3xl bg-white/[0.12] p-5 rounded-2xl text-center text-[13px] font-medium border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] text-white/90 leading-relaxed break-words relative">
            {n.message}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const CountdownWebsite = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [note, setNote] = useState<string>("");
  const [highlightedId, setHighlightedId] = useState<string | number | null>(
    null
  );
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [activeGalaxy, setActiveGalaxy] = useState<Galaxy | null>(null);
  const [activeConstellation, setActiveConstellation] =
    useState<Constellation | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<Star | null>(null);
  const [hoveredMessage, setHoveredMessage] = useState<Star | null>(null);

  const [notes, setNotes] = useState<Star[]>([]);
  const [standaloneConstellations, setStandaloneConstellations] = useState<
    Constellation[]
  >([]);
  const [galaxies, setGalaxies] = useState<Galaxy[]>([]);
  const { scrollYProgress } = useScroll();
  const uiSpring = { stiffness: 500, damping: 50, mass: 0.1 };
  const cinematicSpring = { stiffness: 80, damping: 30, restDelta: 0.001 };
  const timerOpacity = useSpring(
    useTransform(scrollYProgress, [0, 0.08], [1, 0]),
    uiSpring
  );
  const inputOpacity = useSpring(
    useTransform(scrollYProgress, [0.15, 0.3, 0.65, 0.75], [0, 1, 1, 0]),
    uiSpring
  );
  const inputY = useSpring(
    useTransform(scrollYProgress, [0.15, 0.3], [30, 0]),
    uiSpring
  );
  const starPointOpacity = useSpring(
    useTransform(scrollYProgress, [0, 0.8, 0.95], [0.4, 0.6, 1]),
    uiSpring
  );
  const starsClickable = useTransform(
    scrollYProgress,
    [0.85, 0.95],
    ["none", "auto"]
  );
  const hintOpacity = useTransform(scrollYProgress, [0, 0.02], [0.7, 0]);

  const fetchUniverseData = async () => {
    if (!supabase) return;
    const starsRes = await supabase
      .from("stars")
      .select("*")
      .order("created_at", { ascending: true });
    const universeRes = await supabase.from("compiled_universe").select("*");

    console.log("DEBUG: Raw stars loaded from DB ->", starsRes.data);
    console.log(
      "DEBUG: Raw universe lines loaded from DB ->",
      universeRes.data
    );
    const formattedGalaxies: Galaxy[] = [];
    const claimedMessages = new Set<string>();

    if (universeRes.data) {
      universeRes.data.forEach((row: any) => {
        if (!row.is_standalone) {
          const galaxyData = Array.isArray(row.data) ? row.data : [];
          galaxyData.forEach((constellation: any) => {
            const messagesList = constellation.messages || [];
            messagesList.forEach((m: any) => {
              if (m && m.message) claimedMessages.add(m.message.trim());
            });
          });

          formattedGalaxies.push({
            id: row.galaxy_id || row.id,
            name: row.galaxy_name || row.name || "Unknown Galaxy",
            x: row.galaxy_x ?? row.x ?? 50,
            y: row.galaxy_y ?? row.y ?? 50,
            rotationSpeed: row.rotation_speed || 40,
            data: galaxyData,
          });
        }
      });
    }

    if (starsRes.data) {
      const looseStars = starsRes.data.filter((star: any) => {
        if (star.constellation_id) return false;
        if (!star.message) return false;
        return !claimedMessages.has(star.message.trim());
      });
      console.log(
        "DEBUG: Unassigned loose strings available ->",
        looseStars.length
      );

      const TRIGGER_THRESHOLD = 100;
      const STARS_PER_CONSTELLATION = 80;
      const CONSTELLATIONS_PER_GALAXY = 100;

      const unassignedBackgroundStars: Star[] = [];
      const compiledConstellations: Constellation[] = [];
      let processedIndex = 0;

      while (looseStars.length - processedIndex >= TRIGGER_THRESHOLD) {
        const constellationBatch: Star[] = looseStars.slice(
          processedIndex,
          processedIndex + STARS_PER_CONSTELLATION
        );
        processedIndex += STARS_PER_CONSTELLATION;

        const avgX =
          constellationBatch.reduce((sum: number, s: Star) => sum + s.x, 0) /
          constellationBatch.length;
        const avgY =
          constellationBatch.reduce((sum: number, s: Star) => sum + s.y, 0) /
          constellationBatch.length;
        const firstId = constellationBatch[0].id;
        const lastId = constellationBatch[constellationBatch.length - 1].id;
        compiledConstellations.push({
          id: `dynamic-constellation-${firstId}-${lastId}`,
          name: getDeterministicConstellationName(firstId),
          x: parseFloat(avgX.toFixed(2)),
          y: parseFloat(avgY.toFixed(2)),
          drift: 0,
          messages: constellationBatch,
        });
      }

      const remainingLoose = looseStars.slice(processedIndex);
      unassignedBackgroundStars.push(...remainingLoose);

      const verifiedStandaloneConstellations: Constellation[] = [];
      let k = 0;
      while (k < compiledConstellations.length) {
        const remainingConstellations = compiledConstellations.length - k;
        if (remainingConstellations < CONSTELLATIONS_PER_GALAXY) {
          for (let j = k; j < compiledConstellations.length; j++) {
            verifiedStandaloneConstellations.push(compiledConstellations[j]);
          }
          break;
        }

        const galaxyClusterBatch = compiledConstellations.slice(
          k,
          k + CONSTELLATIONS_PER_GALAXY
        );
        const galaxyX =
          galaxyClusterBatch.reduce(
            (sum: number, c: Constellation) => sum + c.x,
            0
          ) / galaxyClusterBatch.length;
        const galaxyY =
          galaxyClusterBatch.reduce(
            (sum: number, c: Constellation) => sum + c.y,
            0
          ) / galaxyClusterBatch.length;
        const galaxySeedKey = galaxyClusterBatch[0].id;

        formattedGalaxies.push({
          id: `grand-milestone-galaxy-${galaxySeedKey}`,
          name: getDeterministicGalaxyName(galaxySeedKey),
          x: parseFloat(galaxyX.toFixed(2)),
          y: parseFloat(galaxyY.toFixed(2)),
          rotationSpeed: 60,
          data: galaxyClusterBatch,
        });
        k += CONSTELLATIONS_PER_GALAXY;
      }

      console.log("DEBUG BALANCING MATRIX:");
      console.log(
        "-> Floating background notes:",
        unassignedBackgroundStars.length
      );
      console.log(
        "-> Free floating clusters:",
        verifiedStandaloneConstellations.length
      );
      console.log("-> Unified milestone galaxies:", formattedGalaxies.length);

      setNotes(unassignedBackgroundStars);
      setStandaloneConstellations(verifiedStandaloneConstellations);
    }

    setGalaxies(formattedGalaxies);
  };

  useEffect(() => {
    const localCheck = localStorage.getItem("galaxy_message_sent");
    if (localCheck === "true") {
      setHasSubmitted(true);
    }
    fetchUniverseData();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const difference = TARGET_DATE - now;
      setTimeLeft({
        days: Math.max(0, Math.floor(difference / (1000 * 60 * 60 * 24))),
        hours: Math.max(0, Math.floor((difference / (1000 * 60 * 60)) % 24)),
        minutes: Math.max(0, Math.floor((difference / 1000 / 60) % 60)),
        seconds: Math.max(0, Math.floor((difference / 1000) % 60)),
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === "Enter" &&
      note.trim() &&
      note.length <= 120 &&
      !hasSubmitted
    ) {
      e.preventDefault();
      const typedMessage = note.trim();
      const generatedX = parseFloat((Math.random() * 92 + 4).toFixed(2));
      const generatedY = parseFloat((Math.random() * 90 + 5).toFixed(2));
      const generatedDrift = parseFloat(((Math.random() - 0.5) * 3).toFixed(2));
      const generatedSize = parseFloat((Math.random() * 1.5 + 1.0).toFixed(2));

      setNote("");
      setHasSubmitted(true);
      localStorage.setItem("galaxy_message_sent", "true");
      if (supabase) {
        const { data, error } = await supabase
          .from("stars")
          .insert([
            {
              message: typedMessage,
              x: generatedX,
              y: generatedY,
              drift: generatedDrift,
              size: generatedSize,
            },
          ])
          .select();
        if (error) {
          console.error("Write error fallback active:", error.message);
          const fallbackEntry: Star = {
            id: `fallback-${Date.now()}`,
            message: typedMessage,
            x: generatedX,
            y: generatedY,
            drift: generatedDrift,
            size: generatedSize,
          };
          setNotes([fallbackEntry]);
        } else {
          await fetchUniverseData();
          if (data && data[0]) {
            setHighlightedId(data[0].id);
            setTimeout(() => setHighlightedId(null), 4000);
          }
        }
      }

      window.scrollTo({
        top: document.documentElement.scrollHeight * 0.95,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="bg-black text-white min-h-[280vh] font-sans selection:bg-white selection:text-black antialiased overflow-x-hidden relative">
      <style>{`.no-scrollbar::-webkit-scrollbar { display: none; }`}</style>

      {/* CONSTELLATION LINE LAYER */}
      <div className="fixed inset-0 z-[5] pointer-events-none">
        <svg className="w-full h-full overflow-visible">
          <motion.g style={{ opacity: starPointOpacity }}>
            {notes.map((n1, i) =>
              notes.slice(i + 1).map((n2) => {
                const dist = Math.sqrt(
                  Math.pow(n1.x - n2.x, 2) + Math.pow(n1.y - n2.y, 2)
                );
                if (dist < 18) {
                  return (
                    <motion.line
                      key={`${n1.id}-${n2.id}`}
                      x1={`${n1.x}%`}
                      y1={`${n1.y}%`}
                      x2={`${n2.x}%`}
                      y2={`${n2.y}%`}
                      stroke="white"
                      strokeWidth="0.5"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{ pathLength: 1, opacity: 0.15 }}
                      transition={{ duration: 2 }}
                    />
                  );
                }
                return null;
              })
            )}
          </motion.g>
        </svg>
      </div>

      {/* OBJECT LAYERS LAYER */}
      <div className="fixed inset-0 z-[6] overflow-hidden pointer-events-none">
        <AnimatePresence>
          {notes.map((n) => (
            <StarNote
              key={n.id}
              n={n}
              highlightedId={highlightedId}
              starPointOpacity={starPointOpacity}
              cinematicSpring={cinematicSpring}
              starsClickable={starsClickable}
            />
          ))}
        </AnimatePresence>

        {/* STANDALONE CONSTELLATIONS */}
        {standaloneConstellations.map((c) => {
          const leftPos = c.x !== undefined ? `${c.x}%` : "50%";
          const topPos = c.y !== undefined ? `${c.y}%` : "50%";
          const isClickDisabled = starsClickable.get() === "none";

          return (
            <motion.div
              key={`standalone-const-${c.id}`}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              className="absolute z-[80]"
              style={{
                left: leftPos,
                top: topPos,
                transform: "translate(-50%, -50%)",
                pointerEvents: isClickDisabled ? "none" : "auto",
                cursor: isClickDisabled ? "default" : "pointer",
              }}
              onClick={(e) => {
                if (isClickDisabled) return;
                e.stopPropagation();
                setActiveConstellation(c);
              }}
            >
              <div className="relative flex items-center justify-center w-8 h-8 group">
                <div className="absolute inset-0 bg-blue-500/10 blur-md rounded-full pointer-events-none" />
                <motion.div
                  className="w-[3px] h-[3px] bg-blue-400 rounded-full shadow-[0_0_8px_3px_rgba(59,130,246,0.6)] group-hover:scale-125 transition-transform duration-300"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                <span className="absolute top-6 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-[0.4em] text-blue-300/80 font-bold whitespace-nowrap bg-black/60 backdrop-blur-sm px-2 py-0.5 rounded-md opacity-0 group-hover:opacity-100 transition-all duration-300">
                  {c.name || "Unnamed Cluster"}
                </span>
              </div>
            </motion.div>
          );
        })}

        {/* SYSTEM GALAXIES */}
        {galaxies.map((g) => {
          const isClickDisabled = starsClickable.get() === "none";

          return (
            <motion.div
              key={`galaxy-node-${g.id}`}
              className="absolute z-[40]"
              style={{
                left: `${g.x}%`,
                top: `${g.y}%`,
                transform: "translate(-50%, -50%)",
                pointerEvents: isClickDisabled ? "none" : "auto",
                cursor: isClickDisabled ? "default" : "pointer",
              }}
              onClick={() => {
                if (isClickDisabled) return;
                setActiveGalaxy(g);
              }}
            >
              <motion.div
                style={{ opacity: starPointOpacity }}
                animate={{ rotate: 360 }}
                transition={{
                  duration: g.rotationSpeed || 40,
                  repeat: Infinity,
                  ease: "linear",
                }}
                className="relative flex items-center justify-center group"
              >
                <div className="w-6 h-6 bg-white/[0.02] blur-xl rounded-full absolute pointer-events-none" />
                <div className="w-[4px] h-[4px] bg-white rounded-full shadow-[0_0_10px_2px_rgba(255,255,255,0.7)] z-10" />
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-10 h-10 border-t-[1.2px] border-white/20 rounded-full mix-blend-screen"
                    style={{
                      transform: `rotate(${i * 120}deg)`,
                      clipPath: "inset(0 45% 45% 0)",
                    }}
                  />
                ))}
                <span className="absolute top-8 left-1/2 -translate-x-1/2 text-[7px] tracking-[0.5em] text-white/40 uppercase whitespace-nowrap font-black opacity-0 group-hover:opacity-100 transition-all duration-300">
                  {g.name}
                </span>
              </motion.div>
            </motion.div>
          );
        })}
      </div>

      {/* TIMER SECTION */}
      <section className="h-screen flex items-center justify-center sticky top-0 z-20 pointer-events-none">
        <motion.div
          style={{ opacity: timerOpacity }}
          className="relative flex items-center justify-center text-center"
        >
          <div className="absolute w-[600px] h-[400px] bg-white/[0.05] blur-[120px] rounded-full" />
          <div className="relative flex gap-8 md:gap-14 text-white/90">
            {Object.entries(timeLeft).map(([label, value]) => (
              <div key={label} className="flex flex-col items-center">
                <span className="tabular-nums text-6xl md:text-9xl font-bold tracking-tighter">
                  {String(value).padStart(2, "0")}
                </span>
                <span className="text-[10px] tracking-[0.8em] mt-6 opacity-40 uppercase font-black">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* INPUT SECTION */}
      <section className="h-screen flex flex-col items-center justify-center sticky top-0 z-40 px-8 pointer-events-none">
        <motion.div
          style={{ opacity: inputOpacity }}
          className="absolute w-[500px] h-[300px] bg-white/[0.07] blur-[100px] rounded-full pointer-events-none"
        />
        <motion.div
          style={{ opacity: inputOpacity, y: inputY }}
          className="w-full max-w-xl pointer-events-auto relative z-10"
        >
          <AnimatePresence mode="wait">
            {!hasSubmitted ? (
              <motion.div
                key="input-form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-2xl md:text-4xl font-light tracking-tight text-center mb-16 text-white/90">
                  What would you do with just 1 year?
                </h2>
                <div className="relative group">
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    onKeyDown={handleSubmit}
                    placeholder="Leave a note..."
                    maxLength={120}
                    className="w-full bg-transparent border-b border-white/20 py-6 text-xl md:text-3xl focus:outline-none focus:border-white transition-all text-center font-light placeholder:text-white/40 cursor-text"
                  />
                  <div className="flex justify-between items-center mt-3 selection:bg-transparent min-h-[16px]">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-white/30 font-semibold">
                      You get one chance — make it count.
                    </span>
                    <AnimatePresence>
                      {note.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, x: 5 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 5 }}
                          transition={{ duration: 0.2 }}
                          className="text-right text-[11px] uppercase tracking-widest text-white/40 font-bold"
                        >
                          {120 - note.length}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="lockout-view"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 100, damping: 25 }}
                className="text-center"
              >
                <h2 className="text-xl md:text-3xl font-light tracking-wide text-white/80 leading-relaxed max-w-md mx-auto">
                  Your intention has been recorded.
                </h2>
                <p className="text-[10px] tracking-[0.4em] text-white/30 uppercase font-black mt-6">
                  One soul • One star
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </section>

      {/* GALAXY MODAL */}
      <AnimatePresence>
        {activeGalaxy && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] flex flex-col items-center justify-center bg-black/95 backdrop-blur-3xl p-6"
          >
            <div className="absolute top-12 flex flex-col items-center gap-2">
              <h3 className="text-white text-xl font-light tracking-[0.5em] uppercase text-center max-w-xl">
                {activeGalaxy.name}
              </h3>
              <button
                onClick={() => setActiveGalaxy(null)}
                className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/40 hover:text-white transition-colors"
              >
                Close System Archive
              </button>
            </div>
            <div
              className="absolute inset-0 z-0"
              onClick={() => setActiveGalaxy(null)}
            />
            <div className="relative z-10 max-w-5xl w-full max-h-[70vh] overflow-y-auto no-scrollbar grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-6 p-8">
              {(activeGalaxy.data || []).map((constellation: Constellation) => (
                <motion.div
                  key={constellation.id}
                  whileHover={{ scale: 1.05 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveConstellation(constellation);
                  }}
                  className="bg-white/[0.03] border border-white/10 p-6 rounded-2xl flex flex-col items-center gap-4 cursor-pointer hover:bg-white/[0.08] hover:border-blue-400/30 transition-all text-center"
                >
                  <div className="w-3 h-3 rounded-full bg-blue-300 shadow-[0_0_10px_#60a5fa]" />
                  <span className="text-[11px] uppercase tracking-[0.2em] text-blue-200 font-medium">
                    {constellation.name}
                  </span>
                  <span className="text-[9px] uppercase tracking-wider text-white/30 font-bold">
                    {constellation.messages?.length || 0} Stars
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CONSTELLATION MODAL */}
      <AnimatePresence>
        {activeConstellation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-black/95 backdrop-blur-3xl p-6"
          >
            <div className="absolute top-12 flex flex-col items-center gap-2">
              <h3 className="text-blue-200 text-lg font-light tracking-[0.4em] uppercase">
                {activeConstellation.name}
              </h3>
              <button
                onClick={() => {
                  setActiveConstellation(null);
                  setSelectedMessage(null);
                  setHoveredMessage(null);
                }}
                className="text-[10px] uppercase tracking-[0.4em] font-bold text-white/40 hover:text-white transition-colors"
              >
                Back to Cluster Space
              </button>
            </div>
            <div
              className="absolute inset-0 z-0"
              onClick={() => {
                setActiveConstellation(null);
                setSelectedMessage(null);
                setHoveredMessage(null);
              }}
            />
            <div className="relative z-10 max-w-4xl w-full h-[55vh] overflow-y-auto no-scrollbar grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-4 p-8">
              {(activeConstellation.messages || []).map((m: Star) => (
                <motion.div
                  key={m.id}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  onMouseEnter={() => setHoveredMessage(m)}
                  onMouseLeave={() => setHoveredMessage(null)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedMessage((prev) =>
                      prev?.id === m.id ? null : m
                    );
                  }}
                  className="aspect-square bg-white/5 border border-white/10 rounded-full flex items-center justify-center cursor-pointer hover:bg-white/20 transition-all"
                >
                  <div
                    className={`w-1 h-1 rounded-full shadow-[0_0_8px_#60a5fa] ${
                      selectedMessage?.id === m.id
                        ? "bg-white scale-150"
                        : "bg-blue-300"
                    }`}
                  />
                </motion.div>
              ))}
            </div>
            <AnimatePresence mode="wait">
              {(selectedMessage || hoveredMessage) && (
                <motion.div
                  key={
                    selectedMessage
                      ? `sel-${selectedMessage.id}`
                      : `hov-${hoveredMessage?.id}`
                  }
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute z-20 pointer-events-none px-4 bottom-16 md:bottom-24 w-full flex justify-center"
                >
                  <div
                    className={`backdrop-blur-2xl p-6 md:p-10 rounded-[2.5rem] w-full max-w-lg text-center shadow-2xl border border-white/10 transition-colors duration-300 ${
                      selectedMessage
                        ? "bg-white text-black border-transparent"
                        : "bg-white/10 text-white/90"
                    }`}
                  >
                    <p className="text-lg md:text-xl font-light leading-relaxed italic break-words">
                      {selectedMessage
                        ? `"${selectedMessage.message}"`
                        : `"${truncateText(hoveredMessage!.message, 25)}"`}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        style={{ opacity: hintOpacity }}
        className="fixed bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-[60] pointer-events-none"
      >
        <span className="text-[10px] uppercase tracking-[0.5em] font-bold text-white/40">
          Scroll
        </span>
        <div className="w-[1px] h-6 bg-gradient-to-b from-white/30 to-transparent" />
      </motion.div>
    </div>
  );
};

export default CountdownWebsite;
